import { BookingStatus, Currency, Language } from "@finbackoffice/enums";
import { HoursRange, IWsMessageFromServer, IWsMessageToServer } from "../common";
import {
  IServerCalendar,
  IServerChatMessageEvent,
  IServerConnectedEvent,
  IServerConnectEvent,
  IServerDepositBatchEvent,
  IServerDepositEvent,
  IServerErrorEvent,
  IServerExchangeRatesLatestEvent,
  IServerExchangeRatesTimeEvent,
  IServerFeedLiveEvent,
  IServerFeedPrematchEvent,
  IServerGameEvent,
  IServerMarket,
  IServerPingEvent,
  IServerSubscribeEvent,
  IServerSubscribeMarket,
  IServerSubscribeTournament,
  IServerTicketEvent,
  IServerTopGamesEvent,
  IServerTournament,
  IServerUnsubscribeEvent,
  IServerUpcomingGamesEvent,
  IServerWalletEvent,
  IServerWithdrawalBatchEvent,
  IServerWithdrawalEvent,
  SERVER_PING,
  SERVER_UNSUBSCRIBE,
} from "../constants/events";
import { WebsocketAdapter } from "./socket.adapter";

type ISocketErrorEvent = {
  error: string;
  message: string;
  statusCode: number;
};

export type IMessagesToServer =
  | IWsMessageToServer<IServerPingEvent, { timestamp: number }>
  | IWsMessageToServer<IServerDepositEvent>
  | IWsMessageToServer<IServerDepositBatchEvent>
  | IWsMessageToServer<IServerWithdrawalEvent>
  | IWsMessageToServer<IServerWithdrawalBatchEvent>
  | IWsMessageToServer<IServerTicketEvent>
  | IWsMessageToServer<IServerChatMessageEvent>
  | IWsMessageToServer<IServerConnectEvent, { language: Language }>
  | IWsMessageToServer<IServerFeedPrematchEvent, { hours: HoursRange; booking_status?: BookingStatus }>
  | IWsMessageToServer<IServerFeedLiveEvent>
  | IWsMessageToServer<IServerSubscribeEvent, { event_id: number; token?: string }>
  | IWsMessageToServer<
      IServerSubscribeTournament,
      { tournament_id: number; from?: number; to?: number; booking_status?: BookingStatus }
    >
  | IWsMessageToServer<
      IServerSubscribeMarket,
      { event_id: number; market_id: number; outcome_id: number; wallet_id?: number }
    >
  | IWsMessageToServer<IServerCalendar, { from?: number; to?: number }>
  | IWsMessageToServer<IServerUnsubscribeEvent>
  | IWsMessageToServer<IServerWalletEvent>
  | IWsMessageToServer<
      IServerExchangeRatesLatestEvent,
      {
        fromCurrency: Currency[];
        toCurrencies: Currency[];
      }
    >
  | IWsMessageToServer<
      IServerExchangeRatesTimeEvent,
      {
        time: string;
        fromCurrency: Currency;
        toCurrencies: Currency[];
      }
    >
  | IWsMessageToServer<
      IServerTopGamesEvent,
      {
        hours: HoursRange;
        fromCurrency: Currency;
        toCurrencies: Currency[];
      }
    >
  | IWsMessageToServer<
      IServerUpcomingGamesEvent,
      {
        hours: HoursRange;
        fromCurrency: Currency;
        toCurrencies: Currency[];
      }
    >;

export type IMessagesFromServer<
  TPaymentMeta = any,
  TTicketResponse = any,
  TFeedConfigResponse = any,
  TLiveFeedResponse = any,
  TIMarketFeedResponse = any,
  TWalletResponse = any,
  TMessageResponse = any,
  TExchangeRatesMapResponse = any,
  TExchangeRateBatchResponse = any,
> =
  | IWsMessageFromServer<IServerWithdrawalEvent, TPaymentMeta>
  | IWsMessageFromServer<IServerWithdrawalBatchEvent, TPaymentMeta[]>
  | IWsMessageFromServer<IServerDepositEvent, TPaymentMeta>
  | IWsMessageFromServer<IServerDepositBatchEvent, TPaymentMeta[]>
  | IWsMessageFromServer<IServerTicketEvent, TTicketResponse>
  | IWsMessageFromServer<IServerConnectedEvent, TFeedConfigResponse>
  | IWsMessageFromServer<IServerFeedPrematchEvent, TLiveFeedResponse>
  | IWsMessageFromServer<IServerFeedLiveEvent, TLiveFeedResponse>
  | IWsMessageFromServer<IServerGameEvent, TLiveFeedResponse>
  | IWsMessageFromServer<IServerTournament, TLiveFeedResponse>
  | IWsMessageFromServer<IServerMarket, TIMarketFeedResponse>
  | IWsMessageFromServer<IServerCalendar, TLiveFeedResponse>
  | IWsMessageFromServer<IServerWalletEvent, TWalletResponse>
  | IWsMessageFromServer<IServerChatMessageEvent, TMessageResponse>
  | IWsMessageFromServer<IServerExchangeRatesLatestEvent, TExchangeRatesMapResponse>
  | IWsMessageFromServer<IServerExchangeRatesTimeEvent, TExchangeRateBatchResponse>
  | IWsMessageFromServer<IServerTopGamesEvent, TLiveFeedResponse>
  | IWsMessageFromServer<IServerUpcomingGamesEvent, TLiveFeedResponse>
  | IWsMessageFromServer<IServerErrorEvent, ISocketErrorEvent>;

export type IMessageFromServerPong = {
  data: {
    timestamp: string;
  };
  event: string;
  rid: string;
};

type ISocketServiceOptions = {
  url: string;
  shouldPing: boolean;
  onOpen: () => void;
  onClose: () => void;
  onMessageError: (message: IMessagesFromServer) => void;
  onMessage: (message: IMessagesFromServer) => void;
};

export class SocketService {
  private ws: WebsocketAdapter<IMessagesFromServer>;
  constructor(private options: ISocketServiceOptions) {
    this.ws = new WebsocketAdapter<IMessagesFromServer>({
      url: options.url,
      shouldPing: options.shouldPing,
      onOpen: this.options.onOpen,
      onClose: this.options.onClose,
      onMessageError: this.options.onMessageError,
      onMessage: this.options.onMessage,
      onPing: () => {
        this.ws.send({
          event: SERVER_PING,
          data: { timestamp: new Date().getTime(), rid: "" },
        });
      },
    });
  }

  public sendMessage(message: IMessagesToServer) {
    this.ws.send(message);
  }

  public startSubscription<T extends IMessagesToServer["event"]>(
    rid: string,
    event: T,
    data?: Record<string, unknown>,
  ) {
    if (!this.ws.isOpen()) {
      return;
    }

    this.sendMessage({
      event,
      data: {
        ...data,
        rid,
      },
    } as IMessagesToServer);
  }

  public endSubscription(rid: string): void {
    if (!this.ws.isOpen()) {
      return;
    }

    this.sendMessage({
      event: SERVER_UNSUBSCRIBE,
      data: {
        rid,
      },
    });
  }

  public destroy() {
    return this.ws.destroy();
  }
}
